import React, { useState, useEffect } from 'react';
import './AdminLLMDashboard.css';

/**
 * Admin LLM Dashboard
 * Comprehensive admin panel for managing user token quotas and viewing analytics
 */
export const AdminLLMDashboard = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    userId: '',
    tokensToAdd: '',
    reason: '',
    isReset: false
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch users with quotas
      const usersResponse = await fetch('/api/admin/llm/users?limit=100&offset=0', {
        credentials: 'include'
      });

      // Fetch system stats
      const statsResponse = await fetch('/api/admin/llm/stats', {
        credentials: 'include'
      });

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users || []);
      }

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setSystemStats(statsData.system);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      alert('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setAdjustmentForm({
      ...adjustmentForm,
      userId: user.id
    });
  };

  const handleAdjustQuota = async (e) => {
    e.preventDefault();

    if (!adjustmentForm.userId || adjustmentForm.tokensToAdd === '' || !adjustmentForm.reason) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch('/api/admin/adjust-quota', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(adjustmentForm)
      });

      if (!response.ok) throw new Error('Failed to adjust quota');

      await response.json();
      alert('Quota adjusted successfully!');

      // Reset form
      setAdjustmentForm({
        userId: '',
        tokensToAdd: '',
        reason: '',
        isReset: false
      });
      setSelectedUser(null);

      // Refresh data
      fetchDashboardData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleResetAllQuotas = async () => {
    if (!window.confirm('Are you sure you want to reset all due quotas? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('/api/admin/reset-quotas', {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to reset quotas');

      const result = await response.json();
      alert(`Success! Reset ${result.result.count} quotas.`);
      fetchDashboardData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="admin-llm-dashboard">
      <div className="dashboard-header">
        <h1>LLM Token Management Dashboard</h1>
        <button onClick={fetchDashboardData} className="btn btn-refresh" disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {/* System Statistics */}
      {systemStats && (
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-label">Total Users</div>
            <div className="stat-value">{systemStats.total_users || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Users with Quotas</div>
            <div className="stat-value">{systemStats.users_with_quotas || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Chat Sessions</div>
            <div className="stat-value">{systemStats.total_chat_sessions || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Tokens Used</div>
            <div className="stat-value">{((systemStats?.total_tokens_used) || 0).toLocaleString()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Cost</div>
            <div className="stat-value">${(parseFloat(systemStats?.total_cost) || 0).toFixed(2)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Last Chat</div>
            <div className="stat-value">{systemStats?.last_chat_date || 'Never'}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 User Quotas
        </button>
        <button
          className={`tab ${activeTab === 'adjustments' ? 'active' : ''}`}
          onClick={() => setActiveTab('adjustments')}
        >
          ⚙️ Quota Management
        </button>
        <button
          className={`tab ${activeTab === 'high-usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('high-usage')}
        >
          ⚠️ High Usage Users
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-actions">
              <button onClick={handleResetAllQuotas} className="btn btn-danger">
                🔄 Reset All Due Quotas
              </button>
            </div>
            {loading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Email</th>
                      <th>Plan</th>
                      <th>Monthly Allowance</th>
                      <th>Used</th>
                      <th>Remaining</th>
                      <th>Usage %</th>
                      <th>Reset Date</th>
                      <th>Today's Chats</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const allowance = user.monthly_token_allowance || 0;
                      const used = user.tokens_used_this_month || 0;
                      const remaining = user.tokens_remaining || 0;
                      const percentUsed = Math.min(
                        (used / allowance) * 100,
                        100
                      );
                      return (
                        <tr key={user.id} className={percentUsed > 80 ? 'warning' : ''}>
                          <td>{user.business_name}</td>
                          <td>{user.email}</td>
                          <td><span className="badge">{user.plan_code}</span></td>
                          <td>{allowance.toLocaleString()}</td>
                          <td>{used.toLocaleString()}</td>
                          <td>{remaining.toLocaleString()}</td>
                          <td>
                            <div className="usage-bar">
                              <div className="usage-fill" style={{ width: `${percentUsed}%` }} />
                              <span className="usage-text">{percentUsed.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td>{user.quota_reset_date ? new Date(user.quota_reset_date).toLocaleDateString() : 'N/A'}</td>
                          <td>{user.total_chats || 0}</td>
                          <td>
                            <button
                              onClick={() => handleSelectUser(user)}
                              className="btn btn-small btn-primary"
                            >
                              Adjust
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Quota Management Tab */}
        {activeTab === 'adjustments' && (
          <div className="adjustments-section">
            <div className="adjustment-form">
              <h3>Adjust User Quota</h3>
              <form onSubmit={handleAdjustQuota}>
                <div className="form-group">
                  <label>User</label>
                  <select
                    value={adjustmentForm.userId}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, userId: e.target.value })}
                    required
                  >
                    <option value="">Select a user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.business_name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Tokens to Add/Subtract</label>
                  <input
                    type="number"
                    value={adjustmentForm.tokensToAdd}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, tokensToAdd: e.target.value })}
                    placeholder="Enter amount (can be negative)"
                    required
                  />
                  <small>Use negative numbers to deduct tokens</small>
                </div>

                <div className="form-group">
                  <label>Reason</label>
                  <textarea
                    value={adjustmentForm.reason}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                    placeholder="Explain the reason for this adjustment"
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="isReset"
                    checked={adjustmentForm.isReset}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, isReset: e.target.checked })}
                  />
                  <label htmlFor="isReset">This is a monthly quota reset</label>
                </div>

                <button type="submit" className="btn btn-primary">
                  Apply Adjustment
                </button>
              </form>

              {selectedUser && (
                <div className="selected-user-info">
                  <h4>Current Quota for {selectedUser.business_name}</h4>
                  <p>Monthly Allowance: <strong>{(selectedUser.monthly_token_allowance || 0).toLocaleString()}</strong></p>
                  <p>Used This Month: <strong>{(selectedUser.tokens_used_this_month || 0).toLocaleString()}</strong></p>
                  <p>Remaining: <strong>{(selectedUser.tokens_remaining || 0).toLocaleString()}</strong></p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* High Usage Tab */}
        {activeTab === 'high-usage' && (
          <div className="high-usage-section">
            <div className="high-usage-users">
              {users
                .filter((u) => {
                  const allowance = u.monthly_token_allowance || 1;
                  const used = u.tokens_used_this_month || 0;
                  return ((used / allowance) * 100) >= 80;
                })
                .sort((a, b) => (b.tokens_used_this_month || 0) - (a.tokens_used_this_month || 0))
                .map((user) => {
                  const allowance = user.monthly_token_allowance || 0;
                  const used = user.tokens_used_this_month || 0;
                  const percentUsed = (used / (allowance || 1)) * 100;
                  return (
                    <div key={user.id} className="high-usage-card">
                      <div className="card-header">
                        <h4>{user.business_name}</h4>
                        <span className={`severity ${percentUsed >= 100 ? 'critical' : 'warning'}`}>
                          {percentUsed >= 100 ? '🔴 Critical' : '🟡 Warning'}
                        </span>
                      </div>
                      <p className="email">{user.email}</p>
                      <div className="usage-details">
                        <div className="detail">
                          <span>Plan:</span> <strong>{user.plan_code}</strong>
                        </div>
                        <div className="detail">
                          <span>Used:</span> <strong>{used.toLocaleString()}</strong>
                        </div>
                        <div className="detail">
                          <span>Limit:</span> <strong>{allowance.toLocaleString()}</strong>
                        </div>
                        <div className="detail">
                          <span>Usage:</span> <strong>{percentUsed.toFixed(1)}%</strong>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setActiveTab('adjustments');
                          handleSelectUser(user);
                        }}
                        className="btn btn-small btn-primary"
                      >
                        Adjust Quota
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLLMDashboard;
